import React, { lazy, Suspense } from 'react'
import PropTypes from 'prop-types'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { CookiesProvider } from 'react-cookie'
import { I18nextProvider, initReactI18next, useTranslation } from 'react-i18next'
import { LoadingOutlined } from '@ant-design/icons'

import { use } from 'i18next'
import HttpBackend from 'i18next-http-backend'
import ChainedBackend from 'i18next-chained-backend'
import resourcesToBackend from 'i18next-resources-to-backend'

import Layout from './Layout'

import apis from './api'
import { translation } from './constant'

import './index.scss'

const AccountPage = lazy(() => import('./pages/account/Account'))
const ContactUs = lazy(() => import('./pages/contact-us/ContactUs'))
const CountrySelectorPage = lazy(() => import('./pages/country-selector/CountrySelector'))
const DsarPage = lazy(() => import('./pages/dsar/Dsar'))
const FaqPage = lazy(() => import('./pages/faq-page/FaqPage'))
const Home = lazy(() => import('./pages/home/Home'))
const HowItWorks = lazy(() => import('./pages/how-it-works/HowItWorks'))
const LoginPage = lazy(() => import('./pages/login/Login'))
const PageNotFound = lazy(() => import('./pages/error/PageNotFound'))
const ProductCatalogPage = lazy(() => import('./pages/product-catalog/ProductCatalog'))
const ProfilePage = lazy(() => import('./pages/profile-page/ProfilePage'))
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy/PrivacyPolicy'))
const ResetPasswordPage = lazy(() => import('./pages/reset-password/ResetPasswordPage'))
const SignUpPage = lazy(() => import('./pages/sign-up/SignUp'))
const ShopAndReturn = lazy(() => import('./pages/shop-and-return/ShopAndReturn'))
const ThankYouPage = lazy(() => import('./pages/thank-you/ThankYouPage'))
const UserPage = lazy(() => import('./pages/user/User'))
const VerifyEmail = lazy(() => import('./pages/verify-email/VerifyEmail'))
const VerificationError = lazy(() => import('./pages/verification-error/VerificationError'))
const WithdrawPage = lazy(() => import('./pages/withdraw/Withdraw'))

const translationLoader = {
  request: (_, url, __, callback) => {
    apis.strapiInstance
      .get(url)
      .then(({ data }) => {
        callback(null, {
          data: data.data.translations,
          status: 200,
        })
      })
      .catch((e) => {
        console.error(e)
        callback(null, {
          status: 404,
        })
      })
  },
}

// TODO Remove when locale params not necessary anymore
const validNavigatorLanguage = ['en-US', 'en-GB', 'en-CA', 'fr-FR', 'ja-JP'].includes(navigator.language)
  ? navigator.language
  : 'en-US'
const defaultLng =
  new URLSearchParams(window.location.search).get('locale') ||
  localStorage.getItem('new_locale') ||
  validNavigatorLanguage

use(initReactI18next)
  .use(ChainedBackend)
  .init({
    backend: {
      backendOptions: [
        {
          loadPath: `${apis.strapiInstance.defaults.baseURL}/translation?locale={{lng}}`,
          request: translationLoader.request,
        },
        {},
      ],
      backends: [
        HttpBackend,
        resourcesToBackend((_, __, callback) => {
          callback(null, translation[defaultLng.split('-')[0]])
        }),
      ],
    },
    fallbackLng: 'fr-FR',
    interpolation: {
      escapeValue: false,
      formatSeparator: ',',
    },
    keySeparator: false,
    languages: ['en-US', 'en-GB', 'en-CA', 'fr-FR', 'ja-JP'],
    lng: defaultLng,
    load: 'currentOnly',
    react: {
      useSuspense: true,
    },
  })

const Restricted = ({ element, allowedLanguages = ['fr-FR'] }) => {
  const { i18n } = useTranslation()

  return allowedLanguages.includes(i18n.language) ? element : <Navigate replace to="/" />
}

Restricted.propTypes = {
  allowedLanguages: PropTypes.arrayOf(PropTypes.string),
  element: PropTypes.element.isRequired,
}

const router = createBrowserRouter([
  {
    element: <CountrySelectorPage />,
    path: '/countries',
  },
  {
    children: [
      { element: <Home />, index: true },
      { element: <CountrySelectorPage />, path: 'countries' },
      { element: <ContactUs />, path: 'contact-us' },
      { element: <DsarPage />, path: 'dsar' },
      { element: <FaqPage />, path: 'faq' },
      { element: <HowItWorks />, path: 'how-it-works' },
      { element: <ProductCatalogPage />, path: 'products' },
      { element: <PrivacyPolicy />, path: 'privacy-policy' },
      { element: <ResetPasswordPage />, path: 'reset-password' },
      { element: <ShopAndReturn />, path: 'shop-and-return' },
      { element: <ThankYouPage />, path: 'thank-you' },
      { element: <Restricted allowedLanguages={['en-CA', 'fr-FR', 'ja-JP']} element={<LoginPage />} />, path: 'login' },
      {
        children: [
          { element: <ProfilePage />, path: 'profile' },
          { element: <AccountPage />, path: 'account' },
          { element: <VerificationError />, path: 'verification-error' },
          { element: <VerifyEmail />, path: 'verify-email' },
          { element: <WithdrawPage />, path: 'withdraw' },
        ],
        element: <UserPage />,
        path: 'user',
      },
      { element: <Restricted element={<SignUpPage />} />, path: 'signup' },
      { element: <PageNotFound />, path: '*' },
    ],
    element: <Layout />,
    path: ':country?',
    shouldRevalidate: () => false,
  },
  { element: <Navigate replace to="/user/profile" />, path: 'profile' },
  { element: <Navigate replace to="/user/account" />, path: 'account' },
  { element: <Navigate replace to="/user/verification-error" />, path: 'verification-error' },
  { element: <Navigate replace to="/user/verify-email" />, path: 'verify-email' },
  { element: <Navigate replace to="/user/withdraw" />, path: 'withdraw' },
  { element: <PageNotFound />, path: '*' },
])

const Root = () => (
  <I18nextProvider>
    <CookiesProvider defaultSetOptions={{ path: '/' }}>
      <Suspense
        fallback={
          <div className="text-center">
            <LoadingOutlined />
          </div>
        }
      >
        <RouterProvider router={router} />
      </Suspense>
    </CookiesProvider>
  </I18nextProvider>
)

export default Root
